import React, { memo, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pages } from "../../Navigation/root";
import { glyphs, frames, colors, fonts } from "../../Styles";
import { QuickEntryModalLvl1, QuickEntryModalLvl2, QuickEntryModalLvl3, PageHeader } from "../../Components";
import { ButtonTypes, QuickEntry, quickEntryChange } from "../../Definitions";
import { MaintainAnimal } from "../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/maintainAnimalInfoCall";
import { useAppSelector } from "../../Store";
import { Graph_QL } from "../../Services";
import { useAnimal } from "../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/useAnimal";
import NewButton from "../../Components/BaseComponents/Buttons/newButton";

export default function FV_QuickEntry() {
  const { t } = useTranslation()
  const [currentQuickEntryLevel, setCurrentQuickEntryLevel] = useState(1);
  const [quickEntryData, setQuickEntryData] = useState<QuickEntry[]>([]);
  const [quickEntryChange, setQuickEntryChange] = useState<quickEntryChange[]>([]);

  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<boolean>(false)

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
  const { data: animalsState, maintainEntries: updateAnimals } = useAnimal(connection);
  const dirtRef = useRef<MaintainAnimal[]>([]);

  // Function to handle Quick Entry Modal based on level
  const ShowQuickEntry = (level: 0 | 1 | 2 | 3) => {
    setCurrentQuickEntryLevel(level);
  };

  // Function to handle "Go Back"
  const onGoBack = () => {
    if (currentQuickEntryLevel === 3) {
      ShowQuickEntry(2); // Go back to Level 2
    } else if (currentQuickEntryLevel === 2) {
      ShowQuickEntry(1); // Go back to Level 1
    }
  }

  const _subheader = (
    currentQuickEntryLevel === 1 ? t("animalPages.animals.selectAnimals", { defaultValue: "select animals" })
      : currentQuickEntryLevel === 2 ? t("animalPages.animals.action", { defaultValue: "action" })
        : t("animalPages.animals.viewChanges", { defaultValue: "view changes" })
  )

  return (<>
    <frames.Page_inner_focus_view>
      <PageHeader topHeader={t(`menuHeaders.${Pages.op_quickEntry}`, { defaultValue: "focus view" })} />
      <frames._page_inner_content>
        <div style={{ display: "grid", gridTemplateRows: "1fr 20px", gridTemplateColumns: "1fr", gap: "20px", height: "100%", paddingTop: "20px", boxSizing: "border-box" }}>
          <div style={{ position: "absolute", top: "53px", ...fonts.header, ...fonts.textShadow, ...fonts.fontSize2, color: colors.greyLightTertiary }}>{_subheader}</div>

          {currentQuickEntryLevel === 1 || currentQuickEntryLevel === 2 ?
            <div style={{ gridArea: "1/1/2/2", borderTop: "2px solid " + colors.greyDarkPrimary, borderBottom: "2px solid " + colors.greyDarkPrimary, padding: "10px 0px", boxSizing: "border-box", overflowY: "scroll", scrollbarWidth: "thin", scrollbarColor: colors.greyDarkPrimary + colors.transparent }}>
              {currentQuickEntryLevel === 1 && <QuickEntryModalLvl1 quickEntryData={quickEntryData} onQuickEntryDataChange={setQuickEntryData} focus_view />}
              {currentQuickEntryLevel === 2 && <QuickEntryModalLvl2 quickEntryChange={quickEntryChange} quickEntryData={quickEntryData} onQuickEntryChange={setQuickEntryChange} focus_view />}
            </div>
            : null}

          {currentQuickEntryLevel === 3 ?
            <div style={{ gridArea: "1/1/2/2", boxSizing: "border-box", overflow: "hidden" }}>
              {currentQuickEntryLevel === 3 && <QuickEntryModalLvl3 quickEntryData={quickEntryData} quickEntryChange={quickEntryChange} dirtyDataRef={dirtRef} focus_view />}
            </div>
            : null}


          <div style={{ gridArea: "2/1/3/2", display: "grid", width: "100%", gridTemplateColumns: "1fr 1px 1fr", gridTemplateRows: "20px", gap: "20px", position: "relative" }}>
            <div style={{ gridArea: "1/1/2/2", display: "flex", justifyContent: "end" }}>
              {!(currentQuickEntryLevel === 1) ?
                <NewButton
                  content={t("buttons.prev", { defaultValue: "Previous" })}
                  startEnhancer={glyphs.Arrow({ rotate: 180 })}
                  onClick={onGoBack}
                  buttonType={ButtonTypes.textButton}
                  focus_view={true}
                  overrides={{
                    baseStyles: { whiteSpace: "nowrap", width: "fit-content" }
                  }}
                />
                : <></>}
            </div>
            <div style={{ gridArea: "1/2/2/3", backgroundColor: colors.greyDarkPrimary }} />
            <div style={{ gridArea: "1/3/2/4" }}>
              <div style={{ width: "100%", display: "flex", justifyContent: "start", paddingBottom: currentQuickEntryLevel === 3 ? "50px" : "0px" }}>
                <NewButton
                  content={(currentQuickEntryLevel === 3) ? "Apply" : t("buttons.next", { defaultValue: "next" })}
                  endEnhancer={(currentQuickEntryLevel === 3) ? undefined : glyphs.Arrow({})}
                  buttonType={ButtonTypes.textButton}
                  focus_view={true}
                  onClick={() => currentQuickEntryLevel === 3
                    ? (() => {
                      if (updateAnimals) {
                        const value = updateAnimals({ data: dirtRef.current });
                        if (value instanceof Promise) {
                          value.then((res) => {
                            if (res === false) {
                              setMessage(t("messages.errorOccurred", { defaultValue: "error occurred" }));
                              setError(true);
                              setTimeout(() => { setMessage(null); setError(false) }, 2000);
                              return;
                            }
                            setMessage(t("messages.changesApplied", { defaultValue: "changes applied" }));
                            setTimeout(() => {
                              setMessage(null);
                              setCurrentQuickEntryLevel(1);
                              setQuickEntryChange([]);
                              setQuickEntryData([]);
                              dirtRef.current = [];
                            }, 2000);
                          });
                        }
                      }
                    })()
                    : (currentQuickEntryLevel === 1 && (quickEntryData.length === 0 || quickEntryData.some(q => q.value.length === 0))) ? (() => {
                      setMessage("missing select");
                      setError(true);
                      setTimeout(() => { setMessage(null); setError(false) }, 2000);
                      return;
                    })()
                      : (currentQuickEntryLevel === 2 && (quickEntryChange.length === 0 || quickEntryChange.some(q => q.value === undefined))) ? (() => {
                        setMessage("missing action");
                        setError(true);
                        setTimeout(() => { setMessage(null); setError(false) }, 2000);
                        return;
                      })()
                        : setCurrentQuickEntryLevel(currentQuickEntryLevel + 1)
                  }
                  overrides={{
                    baseStyles: { whiteSpace: "nowrap", width: "fit-content" }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </frames._page_inner_content>
    </frames.Page_inner_focus_view>

    <MessageElement message={message} error={error} />
  </>);
}

const MessageElement = memo(({ message, error }: { message: string | null, error: boolean }) => {
  const animation = `
    @keyframes slideInOut {
      0% { transform: translateY(100%); opacity: 0; }
      10% { transform: translateY(0); opacity: 1; }
      90% { transform: translateY(0); opacity: 1; }
      100% { transform: translateY(100%); opacity: 0; }
    }
  `

  return (<>
    <style>{animation}</style>
    <div style={{
      position: "absolute",
      left: "10px",
      right: "10px",
      bottom: "20px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "70px",
      textAlign: "center",
      backgroundColor: error ? colors.greyDarkPrimary : colors.systemGreen,
      border: "3px solid " + (error ? colors.systemRed : colors.systemGreen),
      animation: message ? "slideInOut 2s ease-in-out forwards" : "none",
      boxSizing: "border-box",
      borderRadius: "10px",
      zIndex: message ? "9999" : "-1"
    }}>
      <p style={{ ...fonts.header, ...fonts.fontSize3, color: colors.greyLightTertiary }}>{message}</p>
    </div>
  </>)
})
